import { useState, useEffect } from "react";
import type { SortingState } from "@tanstack/react-table";
import { getUserIdFromToken } from "./useDashboardFilters";

const STORAGE_KEY_PREFIX = "dashboard_sort_v1_";

const VALID_SORT_COLUMNS = new Set([
  "company_name",
  "job_title",
  "alignment_score",
  "success_prob",
  "posting_date",
  "deadline_date",
  "applied_date",
  "created_at",
  "application_status",
]);

const DEFAULT_SORT_ITEM = { id: "created_at", desc: true } as const;
const DEFAULT_SORT: SortingState = [DEFAULT_SORT_ITEM];

function isValidSortItem(v: unknown): v is { id: string; desc: boolean } {
  if (!v || typeof v !== "object") return false;
  const s = v as Record<string, unknown>;
  return (
    typeof s.id === "string" &&
    s.id.length > 0 &&
    VALID_SORT_COLUMNS.has(s.id) &&
    typeof s.desc === "boolean"
  );
}

function loadSort(userId: string): SortingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + userId);
    if (!raw) return DEFAULT_SORT;
    const parsed: unknown = JSON.parse(raw);
    if (isValidSortItem(parsed)) {
      return [{ id: parsed.id, desc: parsed.desc }];
    }
  } catch {
    // fall through to default
  }
  return DEFAULT_SORT;
}

export function useDashboardSort() {
  const userId = getUserIdFromToken();
  const [sorting, setSorting] = useState<SortingState>(() => loadSort(userId));

  useEffect(() => {
    const item = sorting.length > 0 ? sorting[0] : DEFAULT_SORT_ITEM;
    localStorage.setItem(
      STORAGE_KEY_PREFIX + userId,
      JSON.stringify({ id: item.id, desc: item.desc })
    );
  }, [sorting, userId]);

  return { sorting, setSorting };
}
